import React, { useState, useEffect, useCallback } from 'react';
import { getSchedules, updateSchedule, runScheduleNow, getScheduleHistory, getRecentScheduleLogs } from '../api';

export default function SchedulerPage() {
  const [schedules, setSchedules] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [historyFor, setHistoryFor] = useState(null); // schedule id or null
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(new Set());
  const [editingInterval, setEditingInterval] = useState({}); // {id: value}

  const refresh = useCallback(async () => {
    try {
      const [sRes, lRes] = await Promise.all([getSchedules(), getRecentScheduleLogs(30)]);
      setSchedules(sRes.data);
      setRecentLogs(lRes.data);
    } catch (err) {
      console.error('Failed to load schedules:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh every 15s
  useEffect(() => {
    const h = setInterval(refresh, 15000);
    return () => clearInterval(h);
  }, [refresh]);

  const toggleEnabled = async (sched) => {
    try {
      await updateSchedule(sched.ScheduleId, { IsEnabled: !sched.IsEnabled });
      refresh();
    } catch (err) {
      alert('Failed to update: ' + (err.response?.data?.error || err.message));
    }
  };

  const saveInterval = async (sched) => {
    const val = editingInterval[sched.ScheduleId];
    if (!val || isNaN(val) || val < 1) return;
    try {
      await updateSchedule(sched.ScheduleId, { IntervalMinutes: parseInt(val, 10) });
      setEditingInterval(prev => { const n = {...prev}; delete n[sched.ScheduleId]; return n; });
      refresh();
    } catch (err) {
      alert('Failed to update interval: ' + (err.response?.data?.error || err.message));
    }
  };

  const triggerRun = async (sched) => {
    setRunning(prev => new Set(prev).add(sched.ScheduleId));
    try {
      await runScheduleNow(sched.ScheduleId);
    } catch (err) {
      alert('Failed to trigger: ' + (err.response?.data?.error || err.message));
    }
    // Refresh after a short delay to pick up the running status
    setTimeout(() => { refresh(); setRunning(prev => { const n = new Set(prev); n.delete(sched.ScheduleId); return n; }); }, 2000);
  };

  const showHistory = async (sched) => {
    if (historyFor === sched.ScheduleId) { setHistoryFor(null); return; }
    setHistoryFor(sched.ScheduleId);
    try {
      const res = await getScheduleHistory(sched.ScheduleId, 20);
      setHistory(res.data);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString() : '—';
  const fmtDuration = (start, end) => {
    if (!start || !end) return '—';
    const ms = new Date(end) - new Date(start);
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
  };

  if (loading) return <div style={styles.loading}>Loading schedules…</div>;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Scheduler</h1>
        <p style={styles.subtitle}>Manage automated integration syncs</p>
      </div>

      {/* Schedule cards */}
      <div style={styles.grid}>
        {schedules.map(s => (
          <div key={s.ScheduleId} style={{...styles.card, borderLeft: `4px solid ${s.IsEnabled ? '#22c55e' : '#64748b'}`}}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>{s.Name}</div>
                <div style={styles.taskKey}>{s.TaskKey}</div>
              </div>
              <div style={styles.cardActions}>
                <button style={{...styles.toggleBtn, background: s.IsEnabled ? '#22c55e' : '#475569'}} onClick={() => toggleEnabled(s)}>
                  {s.IsEnabled ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>

            <div style={styles.cardBody}>
              <div style={styles.row}>
                <span style={styles.label}>Interval:</span>
                <div style={{display:'flex',gap:6,alignItems:'center'}}>
                  <input
                    type="number" min="1"
                    style={styles.intervalInput}
                    value={editingInterval[s.ScheduleId] ?? s.IntervalMinutes}
                    onChange={e => setEditingInterval(prev => ({...prev, [s.ScheduleId]: e.target.value}))}
                    onBlur={() => { if (editingInterval[s.ScheduleId] !== undefined) saveInterval(s); }}
                    onKeyDown={e => { if (e.key === 'Enter') saveInterval(s); }}
                  />
                  <span style={styles.label}>min</span>
                </div>
              </div>
              <div style={styles.row}>
                <span style={styles.label}>Last run:</span>
                <span style={styles.value}>{fmtDate(s.LastRunAt)}</span>
              </div>
              <div style={styles.row}>
                <span style={styles.label}>Status:</span>
                <span style={{...styles.badge, background: s.LastStatus === 'success' ? '#166534' : s.LastStatus === 'error' ? '#991b1b' : '#374151'}}>
                  {s.LastStatus || 'never'}
                </span>
              </div>
              {s.LastError && <div style={styles.error}>{s.LastError}</div>}
              <div style={styles.row}>
                <span style={styles.label}>Next run:</span>
                <span style={styles.value}>{fmtDate(s.NextRunAt)}</span>
              </div>
            </div>

            <div style={styles.cardFooter}>
              <button style={styles.actionBtn} disabled={running.has(s.ScheduleId)} onClick={() => triggerRun(s)}>
                {running.has(s.ScheduleId) ? 'Running…' : '▶ Run Now'}
              </button>
              <button style={styles.actionBtn} onClick={() => showHistory(s)}>
                {historyFor === s.ScheduleId ? 'Hide History' : 'History'}
              </button>
            </div>

            {historyFor === s.ScheduleId && (
              <div style={styles.historyPanel}>
                {history.length === 0 ? <div style={{color:'#94a3b8',fontSize:13}}>No runs yet</div> :
                  <table style={styles.table}>
                    <thead><tr>
                      <th style={styles.th}>Started</th>
                      <th style={styles.th}>Duration</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Error</th>
                    </tr></thead>
                    <tbody>
                      {history.map(h => (
                        <tr key={h.LogId}>
                          <td style={styles.td}>{fmtDate(h.StartedAt)}</td>
                          <td style={styles.td}>{fmtDuration(h.StartedAt, h.FinishedAt)}</td>
                          <td style={styles.td}>
                            <span style={{...styles.badge, background: h.Status === 'success' ? '#166534' : h.Status === 'error' ? '#991b1b' : '#1e40af'}}>
                              {h.Status}
                            </span>
                          </td>
                          <td style={{...styles.td, maxWidth:250, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}} title={h.ErrorMessage}>{h.ErrorMessage || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                }
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Recent activity log */}
      <div style={{marginTop:32}}>
        <h2 style={{color:'#e2e8f0',fontSize:18,marginBottom:12}}>Recent Activity</h2>
        {recentLogs.length === 0 ? <div style={{color:'#94a3b8'}}>No activity yet</div> :
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>Task</th>
              <th style={styles.th}>Started</th>
              <th style={styles.th}>Duration</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Error</th>
            </tr></thead>
            <tbody>
              {recentLogs.map(l => (
                <tr key={l.LogId}>
                  <td style={styles.td}>{l.ScheduleName || l.TaskKey}</td>
                  <td style={styles.td}>{fmtDate(l.StartedAt)}</td>
                  <td style={styles.td}>{fmtDuration(l.StartedAt, l.FinishedAt)}</td>
                  <td style={styles.td}>
                    <span style={{...styles.badge, background: l.Status === 'success' ? '#166534' : l.Status === 'error' ? '#991b1b' : '#1e40af'}}>
                      {l.Status}
                    </span>
                  </td>
                  <td style={{...styles.td, maxWidth:300, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}} title={l.ErrorMessage}>{l.ErrorMessage || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>
    </div>
  );
}

const styles = {
  page: { padding: '24px 32px', maxWidth: 1100, margin: '0 auto' },
  header: { marginBottom: 24 },
  title: { color: '#f1f5f9', fontSize: 28, fontWeight: 700, margin: 0 },
  subtitle: { color: '#94a3b8', fontSize: 14, marginTop: 4 },
  loading: { color: '#94a3b8', padding: 40, textAlign: 'center' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340, 1fr))', gap: 16 },
  card: { background: '#1e293b', borderRadius: 8, overflow: 'hidden' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px 16px 8px' },
  cardTitle: { color: '#f1f5f9', fontWeight: 600, fontSize: 16 },
  taskKey: { color: '#64748b', fontSize: 12, fontFamily: 'monospace', marginTop: 2 },
  cardActions: { display: 'flex', gap: 8 },
  toggleBtn: { border: 'none', color: '#fff', padding: '4px 14px', borderRadius: 12, fontWeight: 600, fontSize: 12, cursor: 'pointer' },
  cardBody: { padding: '4px 16px 12px' },
  row: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
  label: { color: '#94a3b8', fontSize: 13, minWidth: 70 },
  value: { color: '#cbd5e1', fontSize: 13 },
  intervalInput: { width: 60, padding: '3px 6px', borderRadius: 4, border: '1px solid #475569', background: '#0f172a', color: '#e2e8f0', fontSize: 13 },
  badge: { display: 'inline-block', padding: '2px 10px', borderRadius: 10, color: '#fff', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' },
  error: { color: '#fca5a5', fontSize: 12, marginTop: 4, wordBreak: 'break-all' },
  cardFooter: { display: 'flex', gap: 8, padding: '8px 16px 12px', borderTop: '1px solid #334155' },
  actionBtn: { border: '1px solid #475569', background: 'transparent', color: '#94a3b8', padding: '5px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  historyPanel: { padding: '8px 16px 16px', borderTop: '1px solid #334155', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', color: '#94a3b8', padding: '6px 10px', borderBottom: '1px solid #334155', fontWeight: 600 },
  td: { color: '#cbd5e1', padding: '6px 10px', borderBottom: '1px solid #1e293b' },
};
